import { useRef } from 'react';

import styled from 'styled-components';

import { CopyButton, CopyButtonProps } from '@components/copy-button';
import { FormError } from '@components/form-error';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField, TextFieldProps } from '@components/text-field';
import { useRenderedDimensions } from '@hooks/use-rendered-dimensions';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type PrefixedTextFieldProps = TextFieldProps &
  Pick<CopyButtonProps, 'copyLabel'> & { prefix: string };

export const PrefixedTextField = ({ prefix, copyLabel, ...props }: PrefixedTextFieldProps) => {
  const { error, ...rest } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const { width } = useRenderedDimensions(() => <Text text={prefix} />, [prefix]);

  const handleCopy = () => {
    const prefixedText = `${prefix}${inputRef.current?.value ?? ''}`;
    return prefixedText;
  };

  return (
    <Stack space={1}>
      <Stack orientation="horizontal" justify="space-between" space={2}>
        <TextFieldContainer orientation="horizontal">
          <TextField
            isDisabled
            label={rest.label}
            hideLabel
            defaultValue={prefix}
            style={{ width }}
          />
          <TextField ref={inputRef} hideLabel {...rest} />
        </TextFieldContainer>
        <CopyButton getText={handleCopy} copyLabel={copyLabel} />
      </Stack>
      <FormError text={error} />
    </Stack>
  );
};

const TextFieldContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  alignItems: 'flex-end',
})({
  width: '90%',
  '.input-container': {
    borderColor: theme.color.uiTrans10,
  },

  '>:first-child .input-container': {
    backgroundColor: theme.color.uiTrans10,
    borderRightColor: 'transparent',
    borderTopRightRadius: pxToRem(0),
    borderBottomRightRadius: pxToRem(0),

    input: {
      color: theme.color.textSecondary,
    },
    svg: {
      display: 'none',
      width: pxToRem(0),
    },
  },

  '>:nth-child(2)': {
    width: '90%',
    marginLeft: pxToRem(-1),
    '& .input-container': {
      borderTopLeftRadius: pxToRem(0),
      borderBottomLeftRadius: pxToRem(0),
    },
    label: {
      opacity: 0,
    },
  },
});
